import React, { useEffect, useState } from "react";
import {
  collection,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import apiBaseUrl from "../utils/apiBaseUrl";
import { DEFAULT_PACKAGES, formatPackageDuration, mergePackages } from "../utils/packages";

// Full, cleaned, and working AdminDashboard.jsx
// Preserves all your original features (CSV upload, filters, pagination, theme, mobile menu,
// credential add/reset/delete, transactions list/delete/deleteAll, stats, etc.)

export default function AdminDashboard() {
  // Data
  const [credentials, setCredentials] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [packageCatalog, setPackageCatalog] = useState(DEFAULT_PACKAGES);
  const [priceDrafts, setPriceDrafts] = useState({});
  const [newPackage, setNewPackage] = useState({
    name: "",
    price: "",
    durationValue: "30",
    durationUnit: "days",
    devices: "1",
    portal: "standard",
  });
  const [paymentSearch, setPaymentSearch] = useState("");
  const [resendingReference, setResendingReference] = useState("");
  const [checkingOrderId, setCheckingOrderId] = useState("");
  const [deletingOrderId, setDeletingOrderId] = useState("");
  const [orderPaymentDates, setOrderPaymentDates] = useState({});
  const [activeSection, setActiveSection] = useState("overview");
  const [loadingCreds, setLoadingCreds] = useState(true);
  const [loadingTrans, setLoadingTrans] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [packagesLoaded, setPackagesLoaded] = useState(false);

  // Single credential inputs
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [packageId, setPackageId] = useState("wifi-3-days");
  const [description, setDescription] = useState("");
  const [processing, setProcessing] = useState(false);

  // CSV upload errors
  const [csvErrors, setCsvErrors] = useState([]);

  // UI
  const [theme, setTheme] = useState("dark"); // Dark theme by default
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openNavGroups, setOpenNavGroups] = useState({ operations: true });

  const navigate = useNavigate();

  // --- Filters & Pagination states ---
  // Credentials filters
  const [credPackageFilter, setCredPackageFilter] = useState(""); // "" = all
  const [credUsedFilter, setCredUsedFilter] = useState("all"); // all | used | unused
  const [credSearch, setCredSearch] = useState("");

  // Credentials pagination
  const [credCurrentPage, setCredCurrentPage] = useState(1);
  const credItemsPerPage = 10;

  // Transactions filters
  const [transPackageFilter, setTransPackageFilter] = useState(""); // "" = all

  // Transactions pagination
  const [transCurrentPage, setTransCurrentPage] = useState(1);
  const transItemsPerPage = 10;

  const adminSections = [
    { id: "overview", label: "Overview", description: "Inventory summary" },
    { id: "packages", label: "Package prices", description: "Manage pricing" },
    { id: "tickets", label: "Ticket inventory", description: "Upload and manage tickets" },
    { id: "payments", label: "Payments & resend", description: "Check payments and resend" },
  ];
  const currentSection = adminSections.find((section) => section.id === activeSection) || adminSections[0];
  const adminName = auth.currentUser?.displayName || "Administrator";
  const adminEmail = auth.currentUser?.email || "Admin account";
  const adminInitials = adminName
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const packages = packageCatalog;
  const activePackages = packages.filter((pkg) => pkg.active !== false);
  const inactivePackageIds = new Set(packages.filter((pkg) => pkg.active === false).map((pkg) => pkg.id));
  const inactivePackageCredentials = credentials.filter((credential) => inactivePackageIds.has(credential.packageId));
  const dialogTheme = theme === "dark"
    ? { background: "#0f172a", color: "#e2e8f0" }
    : { background: "#ffffff", color: "#0f172a" };
  const notify = (icon, title, text) => Swal.fire({
    ...dialogTheme,
    toast: true,
    position: "top-end",
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: 3500,
    timerProgressBar: true,
    confirmButtonColor: "#0891b2",
    customClass: {
      popup: `rounded-2xl border ${theme === "dark" ? "border-slate-700" : "border-slate-200"} shadow-2xl`,
    },
  });
  const confirmAction = async (title, text, confirmButtonText = "Continue") => {
    const result = await Swal.fire({
      ...dialogTheme,
      icon: "warning",
      title,
      text,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e11d48",
      cancelButtonColor: "#475569",
      reverseButtons: true,
      customClass: {
        popup: `rounded-2xl border ${theme === "dark" ? "border-slate-700" : "border-slate-200"} shadow-2xl`,
      },
    });
    return result.isConfirmed;
  };

  // Redirect if not authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) navigate("/admin/login");
    });
    return unsubscribe;
  }, [navigate]);

  // Fetch helpers
  const fetchCredentials = async () => {
    setLoadingCreds(true);
    try {
      const snap = await getDocs(collection(db, "credentials"));
      setCredentials(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("fetchCredentials error:", err);
    } finally {
      setLoadingCreds(false);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTrans(true);
    try {
      const snap = await getDocs(collection(db, "MtnTransactions"));
      setTransactions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("fetchTransactions error:", err);
    } finally {
      setLoadingTrans(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const snap = await getDocs(collection(db, "packages"));
      const savedPackages = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      const merged = mergePackages(savedPackages, { includeInactive: true });
      setPackageCatalog(merged);
      setPriceDrafts(Object.fromEntries(merged.map((item) => [item.id, String(item.price ?? "")])));
      setPackagesLoaded(true);
    } catch (err) {
      console.error("fetchPackages error:", err);
      notify("error", "Packages unavailable", "Could not load package prices.");
    }
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const snap = await getDocs(collection(db, "orders"));
      setOrders(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (err) {
      console.error("fetchOrders error:", err);
      notify("error", "Orders unavailable", "Could not load customer orders.");
    } finally {
      setOrdersLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    fetchCredentials();
    fetchTransactions();
    fetchPackages();
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePackagePrice = async (pkg) => {
    const price = Number(priceDrafts[pkg.id]);
    if (!Number.isFinite(price) || price <= 0) {
      notify("warning", "Invalid price", "Enter a package price greater than zero.");
      return;
    }
    setProcessing(true);
    try {
      await setDoc(doc(db, "packages", pkg.id), { name: pkg.name, price }, { merge: true });
      setPackageCatalog((current) =>
        current.map((item) => (item.id === pkg.id ? { ...item, price } : item))
      );
      notify("success", "Price updated", `${pkg.name} is now GHS ${price.toFixed(2)}.`);
    } catch (err) {
      console.error("savePackagePrice error:", err);
      notify("error", "Save failed", "Could not save package price.");
    } finally {
      setProcessing(false);
    }
  };

  const createPackage = async () => {
    const name = newPackage.name.trim();
    const price = Number(newPackage.price);
    const duration = Number(newPackage.durationValue);
    const devices = Number(newPackage.devices);
    if (
      !name ||
      !Number.isFinite(price) ||
      price <= 0 ||
      !Number.isInteger(duration) ||
      duration <= 0 ||
      !Number.isInteger(devices) ||
      devices <= 0
    ) {
      notify("warning", "Package details required", "Enter a name, a price greater than zero, and positive whole numbers for duration and devices.");
      return;
    }

    setProcessing(true);
    try {
      const durationLabel = `${duration}${newPackage.durationUnit === "hours" ? "hour" : "day"}${duration === 1 ? "" : "s"}`;
      const basePackageId = `${durationLabel}-${newPackage.portal}`;
      let generatedPackageId = basePackageId;
      let suffix = 2;
      let packageSnapshot = await getDoc(doc(db, "packages", generatedPackageId));
      while (packageSnapshot.exists()) {
        generatedPackageId = `${basePackageId}-${suffix}`;
        suffix += 1;
        packageSnapshot = await getDoc(doc(db, "packages", generatedPackageId));
      }

      const packageData = {
        name,
        price,
        devices,
        portal: newPackage.portal,
        active: true,
        ...(newPackage.durationUnit === "hours" ? { durationHours: duration } : { durationDays: duration }),
      };
      await setDoc(doc(db, "packages", generatedPackageId), packageData);
      const createdPackage = { id: generatedPackageId, ...packageData };
      setPackageCatalog((current) => [...current, createdPackage]);
      setPriceDrafts((current) => ({ ...current, [generatedPackageId]: String(price) }));
      setNewPackage({ name: "", price: "", durationValue: "30", durationUnit: "days", devices: "1", portal: "standard" });
      notify("success", "Package created", `${name} (${generatedPackageId}) is now available on the ${packageData.portal} customer portal.`);
    } catch (err) {
      console.error("createPackage error:", err);
      notify("error", "Create failed", "Could not create the package. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const deletePackage = async (pkg) => {
    const confirmed = await confirmAction(
      "Delete this package?",
      "This removes the package from the catalog. Existing tickets and payment history will be kept.",
      "Delete package"
    );
    if (!confirmed) return;

    setProcessing(true);
    try {
      await setDoc(doc(db, "packages", pkg.id), { active: false }, { merge: true });
      setPackageCatalog((current) =>
        current.map((item) => item.id === pkg.id ? { ...item, active: false } : item)
      );
      setCredPackageFilter((current) => current === pkg.id ? "" : current);
      setPackageId((current) => current === pkg.id
        ? activePackages.find((item) => item.id !== pkg.id)?.id || ""
        : current
      );
      notify("success", "Package deleted", `${pkg.name} was removed from package management and customer portals.`);
    } catch (err) {
      console.error("deletePackage error:", err);
      notify("error", "Delete failed", "Could not delete this package. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const deleteInactivePackageCredentials = async () => {
    if (!packagesLoaded || loadingCreds) {
      notify("warning", "Data still loading", "Wait for packages and ticket inventory to finish loading.");
      return;
    }

    const inactiveIds = new Set(
      packageCatalog.filter((pkg) => pkg.active === false).map((pkg) => pkg.id)
    );
    const staleCredentials = credentials.filter((credential) => inactiveIds.has(credential.packageId));
    if (staleCredentials.length === 0) {
      notify("info", "Nothing to clean up", "There are no tickets linked to inactive packages.");
      return;
    }

    const confirmed = await confirmAction(
      "Delete tickets for inactive packages?",
      `${staleCredentials.length} ticket credential(s) linked to inactive packages will be permanently deleted. Active packages, active-package tickets, orders, and payment records will be kept.`,
      "Delete inactive tickets"
    );
    if (!confirmed) return;

    setProcessing(true);
    try {
      const batchSize = 450;
      for (let start = 0; start < staleCredentials.length; start += batchSize) {
        const batch = writeBatch(db);
        staleCredentials.slice(start, start + batchSize).forEach((credential) => {
          batch.delete(doc(db, "credentials", credential.id));
        });
        await batch.commit();
      }
      setCredentials((current) => current.filter((credential) => !inactiveIds.has(credential.packageId)));
      setCredCurrentPage(1);
      notify("success", "Inactive tickets deleted", `${staleCredentials.length} ticket credential(s) were removed. Active package data was kept.`);
    } catch (err) {
      console.error("deleteInactivePackageCredentials error:", err);
      await fetchCredentials();
      notify("error", "Cleanup failed", "Could not delete all inactive-package tickets. The ticket list was refreshed; check it before retrying.");
    } finally {
      setProcessing(false);
    }
  };

  const resendTicket = async (transactionRecord) => {
    const reference = String(transactionRecord.reference || "").trim();
    const recipient = String(transactionRecord.phone || transactionRecord.assignedTo || "").trim();
    const assignedCredentials = Array.isArray(transactionRecord.credentials)
      ? transactionRecord.credentials
      : transactionRecord.credentials
        ? [transactionRecord.credentials]
        : transactionRecord.username || transactionRecord.password
          ? [{ username: transactionRecord.username, password: transactionRecord.password }]
          : [];
    if (!reference || !recipient || !assignedCredentials.length) {
      notify("warning", "Missing ticket details", "This payment record needs a Paystack reference, phone number, and ticket credentials.");
      return;
    }
    const message = [
      `Hello ${transactionRecord.name || "Customer"}, your Chidiz Wi-Fi ticket is:`,
      `Package: ${transactionRecord.packageName || transactionRecord.packageId || "Wi-Fi"}`,
      ...assignedCredentials.map((credential, index) => (
        `Device ${index + 1}: ${credential.username} / ${credential.password}`
      )),
      `Payment reference: ${reference}`,
    ].join("\n");

    setResendingReference(reference);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Admin session expired. Sign in again.");
      const response = await fetch(`${apiBaseUrl}/api/send-sms/resend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recipient, message, reference }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "Could not resend ticket.");
      }
      notify("success", "Ticket resent", `The ticket SMS was sent to ${recipient}.`);
    } catch (err) {
      console.error("resendTicket error:", err);
      notify("error", "Resend failed", err.message || "Could not resend ticket.");
    } finally {
      setResendingReference("");
    }
  };

  const checkOrderPayment = async (order) => {
    const paymentDate = orderPaymentDates[order.id];
    if (!paymentDate) {
      notify("warning", "Payment date required", "Choose the date the customer paid before checking this order.");
      return;
    }
    setCheckingOrderId(order.id);
    try {
      const response = await fetch(`${apiBaseUrl}/api/paystack/find`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: paymentDate,
          phone: order.phone,
          amount: Number(order.amount),
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "No successful payment matched this order.");
      }
      if (
        result.transaction.currency !== "GHS" ||
        Math.round(Number(result.transaction.amount) * 100) !== Math.round(Number(order.amount) * 100)
      ) {
        throw new Error("The payment amount does not match this order.");
      }
      await updateDoc(doc(db, "orders", order.id), {
        paymentStatus: "verified",
        paystackReference: result.transaction.reference,
        paymentVerifiedAt: new Date(),
      });
      setOrders((current) =>
        current.map((item) => item.id === order.id
          ? { ...item, paymentStatus: "verified", paystackReference: result.transaction.reference }
          : item)
      );
      notify("success", "Payment verified", `${order.name || order.phone}'s order is ready for ticket issue.`);
    } catch (err) {
      console.error("checkOrderPayment error:", err);
      notify("error", "Payment check failed", err.message || "Could not check this payment.");
    } finally {
      setCheckingOrderId("");
    }
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  // Utility: format many timestamp shapes safely
  const getRecordDate = (value) => {
    if (!value) return null;
    if (value.seconds) return new Date(value.seconds * 1000);
    if (typeof value.toDate === "function") return value.toDate();
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatTimestamp = (t) => {
    if (!t) return "-";
    // Firestore Timestamp
    if (t.seconds) return new Date(t.seconds * 1000).toLocaleString();
    // JS Date object
    if (t.toDate) return t.toDate().toLocaleString();
    // ISO / string
    const d = new Date(t);
    if (!isNaN(d)) return d.toLocaleString();
    return "-";
  };

  // Credential & Transaction functions
  const addCredential = async () => {
    if (!username || !password || !packageId) {
      notify("warning", "Missing details", "Fill in the username, password, and package.");
      return;
    }
    setProcessing(true);
    try {
      await addDoc(collection(db, "credentials"), {
        username,
        password,
        packageId,
        description,
        used: false,
        assignedTo: null,
        createdAt: new Date(),
      });
      setUsername("");
      setPassword("");
      setDescription("");
      await fetchCredentials();
      notify("success", "Ticket added", "Credential added successfully.");
    } catch (err) {
      console.error(err);
      notify("error", "Could not add ticket", "Failed to add credential.");
    } finally {
      setProcessing(false);
    }
  };

  const deleteCredential = async (id) => {
    if (!await confirmAction("Delete this ticket?", "This ticket will be permanently removed.", "Delete ticket")) return;
    try {
      await deleteDoc(doc(db, "credentials", id));
      setCredentials((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error(err);
      notify("error", "Delete failed", "Could not delete this ticket.");
    }
  };

  const resetCredential = async (id) => {
    try {
      await updateDoc(doc(db, "credentials", id), {
        used: false,
        assignedTo: null,
      });
      setCredentials((prev) =>
        prev.map((c) => (c.id === id ? { ...c, used: false, assignedTo: null } : c))
      );
      notify("success", "Ticket reset", "The ticket is available to assign again.");
    } catch (err) {
      console.error(err);
      notify("error", "Reset failed", "Could not reset this ticket.");
    }
  };

  const deleteTransaction = async (id) => {
    if (!await confirmAction("Delete transaction?", "This payment record will be permanently removed.", "Delete transaction")) return;
    try {
      await deleteDoc(doc(db, "MtnTransactions", id));
      setTransactions((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error(err);
      notify("error", "Delete failed", "Could not delete this transaction.");
    }
  };

  const deleteOrder = async (order) => {
    const customerName = order.name || order.phone || "this customer";
    if (!await confirmAction(
      "Delete this customer order?",
      `The order for ${customerName} will be permanently removed. This does not delete its transaction record.`,
      "Delete order"
    )) return;

    setDeletingOrderId(order.id);
    try {
      await deleteDoc(doc(db, "orders", order.id));
      setOrders((current) => current.filter((item) => item.id !== order.id));
      setOrderPaymentDates((current) => {
        const next = { ...current };
        delete next[order.id];
        return next;
      });
      notify("success", "Order deleted", `The order for ${customerName} was deleted.`);
    } catch (err) {
      console.error("deleteOrder error:", err);
      notify("error", "Delete failed", "Could not delete this customer order.");
    } finally {
      setDeletingOrderId("");
    }
  };

  const deleteAllTransactions = async () => {
    if (!await confirmAction("Delete all transactions?", "This cannot be undone.", "Delete all")) return;
    try {
      const snap = await getDocs(collection(db, "MtnTransactions"));
      const deletePromises = snap.docs.map((d) => deleteDoc(d.ref));
      await Promise.all(deletePromises);
      setTransactions([]);
      notify("success", "Transactions deleted", "All transaction records were deleted.");
    } catch (err) {
      console.error(err);
      notify("error", "Delete failed", "Could not delete all transactions.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/admin/login");
  };

  const downloadCsvTemplate = () => {
    const rows = [
      ["username", "password", "packageId", "description"],
      ...activePackages.map((pkg) => ["", "", pkg.id, pkg.name || ""]),
    ];
    const csv = rows.map((row) => row.join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "wifi-ticket-upload-template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // --------------------------
  // CSV file upload from device (kept)
  // --------------------------
  // Accepts same formats as earlier:
  // username,password
  // username,password,packageId
  // username,password,packageId,description
  const handleCSVUpload = (e) => {
    const file = e.target.files?.[0];
    setCsvErrors([]);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = String(event.target.result || "");
      // split lines robustly for different OS
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

      // If first line looks like a header (contains "username" and "password"), skip it
      const first = lines[0] || "";
      const startIndex = /username/i.test(first) && /password/i.test(first) ? 1 : 0;

      const rows = [];
      const errors = [];

      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i];
        // split on comma but don't break if description has comma — we'll join extras into description
        const parts = line.split(",").map((p) => p.trim());
        if (parts.length < 2) {
          errors.push(`Line ${i + 1}: missing username/password.`);
          continue;
        }
        const usernameVal = parts[0];
        const passwordVal = parts[1];
        const packageVal = parts[2] || packageId;
        // join remaining parts as description (so description can contain commas)
        const descriptionVal = parts.slice(3).join(",") || parts[3] || "";

        if (!usernameVal || !passwordVal) {
          errors.push(`Line ${i + 1}: username or password empty.`);
          continue;
        }

        rows.push({
          username: usernameVal,
          password: passwordVal,
          packageId: packageVal,
          description: descriptionVal,
        });
      }

      if (rows.length === 0) {
        setCsvErrors(errors.length ? errors : ["No valid rows found in CSV."]);
        return;
      }

      setProcessing(true);
      try {
        const promises = rows.map((r) =>
          addDoc(collection(db, "credentials"), {
            username: r.username,
            password: r.password,
            packageId: r.packageId,
            description: r.description,
            used: false,
            assignedTo: null,
            createdAt: new Date(),
          })
        );
        await Promise.all(promises);

        // refresh credentials
        await fetchCredentials();

        if (errors.length) setCsvErrors(errors);
        else setCsvErrors([]);

        notify("success", "Tickets uploaded", `Uploaded ${rows.length} credential(s) from CSV.`);
      } catch (err) {
        console.error("CSV upload error:", err);
        notify("error", "Upload failed", "Could not upload the CSV tickets.");
      } finally {
        setProcessing(false);
      }
    };

    reader.readAsText(file);
    // clear the input value so same file can be reselected later if needed
    e.target.value = "";
  };

  // Set container class based on theme
  const containerClass =
    theme === "dark"
      ? "min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-blue-950 text-slate-100 [&_button]:border [&_button]:border-slate-600"
      : "min-h-screen bg-gradient-to-br from-slate-100 via-sky-50 to-indigo-100 text-slate-900 [&_button]:border [&_button]:border-slate-300";

  const sectionClass =
    theme === "dark"
      ? "bg-slate-900/80 backdrop-blur-xl border border-slate-700/80 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-slate-950/30 mb-6"
      : "bg-white/90 border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-xl shadow-slate-300/40 mb-6";

  const tableClass =
    theme === "dark" ? "w-full text-left text-sm text-slate-200" : "w-full text-left text-sm text-slate-700";

  // --------------------------
  // Derived / filtered data & stats
  // --------------------------
  const totalCredentials = credentials.length;
  const availableCredentials = credentials.filter((c) => !c.used).length;
  const usedCredentials = credentials.filter((c) => !!c.used).length;
  const totalPaymentValue = transactions.reduce((total, transaction) => {
    const amount = Number(transaction.amount);
    return total + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const recentPayments = [...transactions]
    .sort((first, second) => {
      const firstDate = getRecordDate(first.assignedAt || first.createdAt || first.timestamp || first.date);
      const secondDate = getRecordDate(second.assignedAt || second.createdAt || second.timestamp || second.date);
      return (secondDate?.getTime() || 0) - (firstDate?.getTime() || 0);
    })
    .slice(0, 5);
  const weeklyPayments = Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (6 - index));
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);
    const value = transactions.reduce((total, transaction) => {
      const paymentDate = getRecordDate(transaction.assignedAt || transaction.createdAt || transaction.timestamp || transaction.date);
      const amount = Number(transaction.amount);
      return paymentDate >= date && paymentDate < nextDate && Number.isFinite(amount) ? total + amount : total;
    }, 0);
    return {
      label: date.toLocaleDateString(undefined, { weekday: "short" }),
      value,
    };
  });
  const weeklyPaymentPeak = Math.max(...weeklyPayments.map((day) => day.value), 1);

  // Credentials filtering
  const filteredCredentials = credentials
    .filter((c) => (credPackageFilter ? c.packageId === credPackageFilter : true))
    .filter((c) =>
      credUsedFilter === "all" ? true : credUsedFilter === "used" ? !!c.used : !c.used
    )
    .filter((c) => (credSearch ? c.username.toLowerCase().includes(credSearch.toLowerCase()) : true));

  const credTotalPages = Math.max(1, Math.ceil(filteredCredentials.length / credItemsPerPage));
  const credCurrentSlice = filteredCredentials.slice(
    (credCurrentPage - 1) * credItemsPerPage,
    credCurrentPage * credItemsPerPage
  );

  // Transactions filtering (by package)
  const filteredTransactions = transactions.filter((t) =>
    (transPackageFilter ? t.packageId === transPackageFilter : true) &&
    (!paymentSearch ||
      [
        t.name,
        t.phone,
        t.reference,
        t.packageId,
        t.packageName,
        t.amount,
        t.credentials?.[0]?.username,
        t.username,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(paymentSearch.toLowerCase()))
  );

  const filteredOrders = orders.filter((order) =>
    !paymentSearch ||
    [
      order.name,
      order.phone,
      order.packageId,
      order.packageName,
      order.amount,
      order.status,
      order.paystackReference,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(paymentSearch.toLowerCase())
  );

  const transTotalPages = Math.max(1, Math.ceil(filteredTransactions.length / transItemsPerPage));
  const transCurrentSlice = filteredTransactions.slice(
    (transCurrentPage - 1) * transItemsPerPage,
    transCurrentPage * transItemsPerPage
  );

  // Ensure current pages are within bounds when data changes
  useEffect(() => {
    setCredCurrentPage((p) => Math.min(p, credTotalPages));
  }, [credTotalPages]);

  useEffect(() => {
    setTransCurrentPage((p) => Math.min(p, transTotalPages));
  }, [transTotalPages]);

  useEffect(() => {
    const groupBySection = {
      packages: "operations",
      tickets: "operations",
      payments: "insights",
    };
    const group = groupBySection[activeSection];
    if (group) {
      setOpenNavGroups((current) => current[group] ? current : { ...current, [group]: true });
    }
  }, [activeSection]);

  return (
    <div className={`${containerClass} flex`}>
      {mobileMenuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-slate-950/70 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close navigation menu"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-[248px] shrink-0 flex-col border-r border-white/10 bg-[#071b2e] text-slate-200 shadow-2xl shadow-slate-950/40 transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-24 items-center border-b border-white/10 px-5">
          <div className="flex h-14 w-full items-center gap-2.5 rounded-xl bg-blue-700/90 px-3 shadow-lg shadow-blue-950/30">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-lg font-black text-cyan-200">
              C
            </span>
            <span className="leading-tight">
              <span className="block text-lg font-extrabold tracking-wide text-white">CHIDIZ</span>
              <span className="block text-[10px] font-bold tracking-[0.24em] text-blue-100">WI-FI ADMIN</span>
            </span>
          </div>
          <button
            type="button"
            className="absolute right-3 top-4 rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m18 6-12 12M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav aria-label="Admin dashboard pages" className="flex-1 overflow-y-auto px-3 py-5">
          <p className="mb-2 px-3 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Workspace</p>
          <button
            type="button"
            onClick={() => {
              setActiveSection("overview");
              setMobileMenuOpen(false);
            }}
            aria-current={activeSection === "overview" ? "page" : undefined}
            className={`mb-2 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] font-bold transition ${
              activeSection === "overview"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-950/40"
                : "text-slate-300 hover:bg-white/5 hover:text-white"
            }`}
          >
            <span className={`flex h-8 w-8 items-center justify-center rounded-md ${activeSection === "overview" ? "bg-blue-500" : "bg-slate-700/70"}`}>
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
                <rect x="13.5" y="3.5" width="7" height="5" rx="1.5" />
                <rect x="13.5" y="11.5" width="7" height="9" rx="1.5" />
                <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
              </svg>
            </span>
            Overview
          </button>

          <p className="mb-2 px-3 pt-2 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Management</p>
          {[
            {
              id: "operations",
              label: "Operations",
              active: ["packages", "tickets"].includes(activeSection),
              items: adminSections.filter((section) => ["packages", "tickets"].includes(section.id)),
              icon: (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="5" y="4" width="14" height="17" rx="2" />
                  <path d="M9 4V3h6v1M9 9h6M9 13h6M9 17h3" />
                </svg>
              ),
            },
            {
              id: "insights",
              label: "Insights",
              active: activeSection === "payments",
              items: adminSections.filter((section) => section.id === "payments"),
              icon: (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M4 20V5M4 20h17M8 16v-4M13 16V8M18 16V4" />
                </svg>
              ),
            },
            {
              id: "administration",
              label: "Administration",
              active: false,
              items: [{ id: "users", label: "User management" }],
              icon: (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="9" cy="8" r="3.5" />
                  <path d="M3 20v-1.5a6 6 0 0 1 12 0V20M17 8h4M19 6v4M17 16a4 4 0 0 1 4 4" />
                </svg>
              ),
            },
          ].map((group) => {
            const expanded = !!openNavGroups[group.id];
            return (
              <div key={group.id} className="mb-1">
                <button
                  type="button"
                  onClick={() => setOpenNavGroups((current) => ({ ...current, [group.id]: !current[group.id] }))}
                  aria-expanded={expanded}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] font-bold transition ${
                    group.active ? "text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <span className={`flex h-8 w-8 items-center justify-center rounded-md ${group.active ? "bg-blue-600 text-white" : "bg-slate-700/70 text-slate-300"}`}>
                    {group.icon}
                  </span>
                  <span className="flex-1">{group.label}</span>
                  <svg viewBox="0 0 24 24" className={`h-4 w-4 text-slate-500 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
                {expanded && (
                  <div className="ml-6 border-l border-slate-700/80 py-1 pl-3">
                    {group.items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          if (item.id === "users") navigate("/admin/users");
                          else setActiveSection(item.id);
                          setMobileMenuOpen(false);
                        }}
                        aria-current={activeSection === item.id ? "page" : undefined}
                        className={`mb-0.5 block w-full rounded-md px-3 py-2 text-left text-xs transition ${
                          activeSection === item.id
                            ? "bg-blue-600/15 font-semibold text-blue-300"
                            : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-3">
          <div className="mb-2 flex items-center gap-2.5 rounded-lg bg-white/[0.06] p-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-xs font-extrabold text-white">
              {adminInitials || "AD"}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold text-white">{adminName}</span>
              <span className="block truncate text-xs text-slate-400">{adminEmail}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-semibold text-rose-300 transition hover:bg-rose-500/10 hover:text-rose-200"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M10 17l5-5-5-5M15 12H3M12 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-6" />
            </svg>
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-h-screen min-w-0 flex-1 px-4 pb-8 pt-5 sm:px-7 lg:px-10">
        <motion.header
          className="mb-8 flex min-h-16 items-center justify-between gap-4 border-b border-white/10 pb-5"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              className={`rounded-xl border p-2.5 transition lg:hidden ${theme === "dark" ? "border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"}`}
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div>
              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${theme === "dark" ? "text-cyan-300" : "text-blue-700"}`}>
                Management console
              </p>
              <h1 className={`mt-1 truncate text-2xl font-extrabold sm:text-3xl ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                {currentSection.label}
              </h1>
              <p className={`mt-1 hidden text-sm sm:block ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                {currentSection.description}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className={`shrink-0 rounded-xl border px-3 py-2 text-xs font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:px-4 sm:py-2.5 sm:text-sm ${
              theme === "dark" ? "border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700 focus-visible:ring-offset-slate-900" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:ring-offset-white"
            }`}
          >
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
        </motion.header>

      <AnimatePresence mode="wait" initial={false}>
      {activeSection === "overview" && (
      <motion.div
        key="overview"
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -24 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
      <motion.section className={sectionClass} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className={`mb-6 overflow-hidden rounded-2xl border p-6 sm:p-8 ${
          theme === "dark" ? "border-blue-400/20 bg-gradient-to-r from-blue-950/80 via-slate-900 to-slate-900" : "border-blue-100 bg-gradient-to-r from-blue-50 to-white"
        }`}>
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Live workspace
              </div>
              <h2 className={`text-2xl font-extrabold sm:text-3xl ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                Welcome, {adminName}
              </h2>
              <p className={`mt-2 max-w-xl text-sm leading-relaxed ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                fetchCredentials();
                fetchTransactions();
                fetchPackages();
                fetchOrders();
              }}
              className="rounded-xl border border-blue-400/20 bg-blue-500/15 px-4 py-2.5 text-sm font-semibold text-blue-200 transition hover:bg-blue-500/25"
            >
              Refresh overview
            </button>
          </div>
        </div>

        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "Total tickets", value: totalCredentials, note: "All credential records", tint: "text-blue-300", icon: "◈" },
            { label: "Available", value: availableCredentials, note: `${totalCredentials ? Math.round((availableCredentials / totalCredentials) * 100) : 0}% ready to assign`, tint: "text-emerald-300", icon: "✓" },
            { label: "Assigned / used", value: usedCredentials, note: `${transactions.length} payment records`, tint: "text-amber-300", icon: "↗" },
            { label: "Payment value", value: `GHS ${totalPaymentValue.toFixed(2)}`, note: `${transactions.length} recorded payments`, tint: "text-violet-300", icon: "₵" },
          ].map((stat) => (
            <div key={stat.label} className={`rounded-2xl border p-5 transition hover:-translate-y-0.5 ${
              theme === "dark" ? "border-slate-700/80 bg-slate-900/80 hover:border-slate-600" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"
            }`}>
              <div className="flex items-start justify-between gap-3">
                <p className={`text-sm font-semibold ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{stat.label}</p>
                <span className={`flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 text-lg font-bold ${stat.tint}`}>{stat.icon}</span>
              </div>
              <p className={`mt-3 truncate text-2xl font-extrabold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>{stat.value}</p>
              <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>{stat.note}</p>
            </div>
          ))}
        </div>

        <div className="mb-6 grid gap-5 xl:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.7fr)]">
          <section className={`rounded-2xl border p-5 sm:p-6 ${theme === "dark" ? "border-slate-700/80 bg-slate-900/80" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"}`}>
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-bold">Payment activity</h3>
                <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>Recorded payment value · last 7 days</p>
              </div>
              <span className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${theme === "dark" ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-500"}`}>GHS</span>
            </div>
            <div className="flex h-40 items-end gap-2 sm:gap-4">
              {weeklyPayments.map((day) => (
                <div key={day.label} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-2">
                  <span className={`text-[10px] ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>
                    {day.value > 0 ? day.value.toFixed(0) : ""}
                  </span>
                  <div className="flex h-28 w-full items-end overflow-hidden rounded-t-lg bg-slate-800/70">
                    <div
                      className="w-full rounded-t-lg bg-gradient-to-t from-blue-700 to-cyan-400 transition-all duration-500"
                      style={{ height: `${day.value ? Math.max((day.value / weeklyPaymentPeak) * 100, 8) : 3}%` }}
                      title={`${day.label}: GHS ${day.value.toFixed(2)}`}
                    />
                  </div>
                  <span className={`text-[10px] font-medium ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>{day.label}</span>
                </div>
              ))}
            </div>
          </section>

          <section className={`rounded-2xl border p-5 sm:p-6 ${theme === "dark" ? "border-slate-700/80 bg-slate-900/80" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"}`}>
            <div className="mb-5">
              <h3 className="font-bold">Inventory health</h3>
              <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>Ticket assignment status</p>
            </div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>Available tickets</span>
              <span className="font-bold">{availableCredentials} <span className="font-normal text-slate-500">/ {totalCredentials}</span></span>
            </div>
            <div className={`h-2.5 overflow-hidden rounded-full ${theme === "dark" ? "bg-slate-800" : "bg-slate-100"}`}>
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all" style={{ width: `${totalCredentials ? (availableCredentials / totalCredentials) * 100 : 0}%` }} />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className={`rounded-xl p-3 ${theme === "dark" ? "bg-slate-800/70" : "bg-slate-50"}`}>
                <p className="text-xs text-slate-500">Available</p>
                <p className="mt-1 text-xl font-bold text-emerald-400">{availableCredentials}</p>
              </div>
              <div className={`rounded-xl p-3 ${theme === "dark" ? "bg-slate-800/70" : "bg-slate-50"}`}>
                <p className="text-xs text-slate-500">Used</p>
                <p className="mt-1 text-xl font-bold text-amber-400">{usedCredentials}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setActiveSection("tickets")}
              className="mt-5 w-full rounded-xl border border-blue-500/20 bg-blue-500/10 px-4 py-2.5 text-sm font-semibold text-blue-300 transition hover:bg-blue-500/20"
            >
              Manage ticket inventory →
            </button>
          </section>
        </div>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(250px,0.6fr)]">
          <section className={`rounded-2xl border p-5 sm:p-6 ${theme === "dark" ? "border-slate-700/80 bg-slate-900/80" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"}`}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold">Recent payments</h3>
                <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>Most recently recorded transactions</p>
              </div>
              <button type="button" onClick={() => setActiveSection("payments")} className="text-xs font-semibold text-blue-400 hover:text-blue-300">
                View all →
              </button>
            </div>
            {loadingTrans ? (
              <p className="py-8 text-center text-sm text-slate-500">Loading payments…</p>
            ) : recentPayments.length ? (
              <div className="divide-y divide-slate-700/60">
                {recentPayments.map((payment) => (
                  <div key={payment.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-300">₵</span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold">{payment.name || payment.phone || "Customer payment"}</span>
                        <span className="mt-0.5 block truncate text-xs text-slate-500">{payment.packageName || payment.packageId || payment.reference || "Wi-Fi package"}</span>
                      </span>
                    </div>
                    <span className="text-right">
                      <span className="block text-sm font-bold">GHS {Number(payment.amount || 0).toFixed(2)}</span>
                      <span className="mt-0.5 block text-[10px] text-slate-500">{formatTimestamp(payment.assignedAt || payment.createdAt || payment.timestamp || payment.date)}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-700 px-4 py-10 text-center text-sm text-slate-500">No payment records yet.</div>
            )}
          </section>
          <section className={`rounded-2xl border p-5 sm:p-6 ${theme === "dark" ? "border-slate-700/80 bg-slate-900/80" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"}`}>
            <h3 className="font-bold">Quick actions</h3>
            <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>Jump back into your workflows</p>
            <div className="mt-4 space-y-2">
              {[
                { label: "Manage packages", description: "Update pricing and plans", section: "packages", icon: "◇" },
                { label: "Add Wi-Fi tickets", description: "Create or import credentials", section: "tickets", icon: "+" },
                { label: "Review payments", description: "Search and resend tickets", section: "payments", icon: "↗" },
              ].map((action) => (
                <button
                  key={action.section}
                  type="button"
                  onClick={() => setActiveSection(action.section)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${
                    theme === "dark" ? "border-slate-800 bg-slate-950/40 hover:border-blue-500/40 hover:bg-blue-500/5" : "border-slate-200 bg-slate-50 hover:border-blue-200 hover:bg-blue-50"
                  }`}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/15 text-lg font-bold text-blue-300">{action.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">{action.label}</span>
                    <span className="mt-0.5 block truncate text-[11px] text-slate-500">{action.description}</span>
                  </span>
                  <span className="text-slate-500">→</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </motion.section>
      </motion.div>
      )}

      {activeSection === "packages" && (
      <motion.div
        key="packages"
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -24 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
      <motion.section className={sectionClass} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className={`mb-6 overflow-hidden rounded-2xl border p-6 sm:p-8 ${
          theme === "dark" ? "border-blue-400/20 bg-gradient-to-br from-blue-950/70 via-slate-900 to-slate-900" : "border-blue-100 bg-gradient-to-br from-blue-50 to-white"
        }`}>
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-400/20 bg-blue-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-300">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
                Product catalog
              </div>
              <h2 className={`text-2xl font-extrabold sm:text-3xl ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                Package management
              </h2>
              <p className={`mt-2 max-w-2xl text-sm leading-relaxed ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>

              </p>
            </div>
            <div className="flex gap-3">
              <div className={`rounded-xl border px-4 py-3 ${theme === "dark" ? "border-white/10 bg-white/[0.04]" : "border-blue-100 bg-white/80"}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Active plans</p>
                <p className={`mt-1 text-xl font-extrabold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>{activePackages.length}</p>
              </div>
              <div className={`rounded-xl border px-4 py-3 ${theme === "dark" ? "border-white/10 bg-white/[0.04]" : "border-blue-100 bg-white/80"}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Portals</p>
                <p className={`mt-1 text-xl font-extrabold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>02</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-7">
          <div className={`rounded-2xl border p-5 sm:p-6 ${
            theme === "dark" ? "border-slate-700/80 bg-slate-900/80" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"
          }`}>
            <div className="mb-5 flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </span>
              <div>
                <h3 className="font-bold">Create a package</h3>
                <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>Configure a plan and publish it to a customer portal.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="sm:col-span-2">
                <span className={`mb-1.5 block text-[11px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Package name</span>
                <input
                  aria-label="New package name"
                  placeholder="e.g. Monthly Unlimited"
                  value={newPackage.name}
                  onChange={(event) => setNewPackage((current) => ({ ...current, name: event.target.value }))}
                  className={`w-full rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-600 focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20"}`}
                />
              </label>
              <label>
                <span className={`mb-1.5 block text-[11px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Price · GHS</span>
                <input
                  aria-label="Package price in GHS"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={newPackage.price}
                  onChange={(event) => setNewPackage((current) => ({ ...current, price: event.target.value }))}
                  className={`w-full rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-600 focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"}`}
                />
              </label>
              <label>
                <span className={`mb-1.5 block text-[11px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Duration</span>
                <span className="flex gap-2">
                  <input
                    aria-label="Package duration"
                    type="number"
                    min="1"
                    step="1"
                    value={newPackage.durationValue}
                    onChange={(event) => setNewPackage((current) => ({ ...current, durationValue: event.target.value }))}
                    className={`min-w-0 flex-1 rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"}`}
                  />
                  <select
                    aria-label="Duration unit"
                    value={newPackage.durationUnit}
                    onChange={(event) => setNewPackage((current) => ({ ...current, durationUnit: event.target.value }))}
                    className={`rounded-xl border px-3 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"}`}
                  >
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </span>
              </label>
              <label>
                <span className={`mb-1.5 block text-[11px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Devices allowed</span>
                <input
                  aria-label="Number of devices"
                  type="number"
                  min="1"
                  step="1"
                  value={newPackage.devices}
                  onChange={(event) => setNewPackage((current) => ({ ...current, devices: event.target.value }))}
                  className={`w-full rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"}`}
                />
              </label>
              <label>
                <span className={`mb-1.5 block text-[11px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Customer portal</span>
                <select
                  aria-label="Customer portal"
                  value={newPackage.portal}
                  onChange={(event) => setNewPackage((current) => ({ ...current, portal: event.target.value }))}
                  className={`w-full rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"}`}
                >
                  <option value="standard">Standard portal</option>
                  <option value="migration">Immigration portal</option>
                </select>
              </label>
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p className={`max-w-sm text-[11px] leading-relaxed ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>
                Package IDs are generated from duration and portal, for example <span className="font-mono">3days-standard</span>.
              </p>
              <button
                type="button"
                onClick={createPackage}
                disabled={processing}
                className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-950/30 transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {processing ? "Creating…" : "Create package"}
                {!processing && <span className="ml-2" aria-hidden="true">→</span>}
              </button>
            </div>
          </div>

        </div>

        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className={`text-lg font-bold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>Active packages</h3>
            <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>Manage visible plans and their customer prices.</p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${theme === "dark" ? "bg-slate-800 text-slate-400" : "bg-slate-200 text-slate-600"}`}>
            {activePackages.length} {activePackages.length === 1 ? "plan" : "plans"}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          {activePackages.map((pkg, index) => (
            <article key={pkg.id} className={`overflow-hidden rounded-xl border transition hover:-translate-y-0.5 ${
              theme === "dark" ? "border-slate-700/80 bg-slate-900/80 hover:border-blue-500/40" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50 hover:border-blue-200"
            }`}>
              <div className={`h-1 bg-gradient-to-r ${index % 3 === 0 ? "from-blue-500 to-cyan-400" : index % 3 === 1 ? "from-violet-500 to-fuchsia-400" : "from-emerald-500 to-teal-400"}`} />
              <div className="p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className={`truncate text-sm font-bold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>{pkg.name || pkg.id}</h4>
                    <p className="mt-0.5 truncate font-mono text-[9px] text-slate-500" title={pkg.id}>{pkg.id}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider ${
                      theme === "dark" ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                    }`}>Active</span>
                    <button
                      type="button"
                      onClick={() => deletePackage(pkg)}
                      disabled={processing}
                      aria-label={`Remove ${pkg.name || pkg.id}`}
                      title="Remove package"
                      className={`rounded-md p-1 text-xs transition disabled:opacity-50 ${
                        theme === "dark" ? "text-slate-500 hover:bg-rose-500/10 hover:text-rose-300" : "text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      }`}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <p className={`mt-1.5 truncate text-[10px] ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                  <span className="capitalize">{pkg.portal || "standard"}</span>
                  <span className="px-1.5 text-slate-400">·</span>
                  {formatPackageDuration(pkg) || "Duration not set"}
                  <span className="px-1.5 text-slate-400">·</span>
                  {pkg.devices || 1} device{Number(pkg.devices || 1) === 1 ? "" : "s"}
                </p>
                <div className={`mt-3 flex items-center gap-1.5 border-t pt-2.5 ${theme === "dark" ? "border-slate-800" : "border-slate-100"}`}>
                  <label className={`shrink-0 text-[9px] font-semibold ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`} htmlFor={`price-${pkg.id}`}>
                    Price
                  </label>
                  <span className={`rounded-md px-2 py-1.5 text-[10px] font-bold ${theme === "dark" ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-500"}`}>GHS</span>
                    <input
                      id={`price-${pkg.id}`}
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={priceDrafts[pkg.id] ?? ""}
                      onChange={(event) => setPriceDrafts((current) => ({ ...current, [pkg.id]: event.target.value }))}
                      className={`min-w-0 flex-1 rounded-md border px-2 py-1.5 text-xs font-semibold outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950 text-white focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"}`}
                    />
                    <button
                      onClick={() => savePackagePrice(pkg)}
                      disabled={processing}
                      className="rounded-md bg-blue-600 px-2.5 py-1.5 text-[10px] font-bold text-white transition hover:bg-blue-500 disabled:opacity-50"
                    >
                      Save
                    </button>
                </div>
              </div>
            </article>
          ))}
        </div>
        {activePackages.length === 0 && (
          <div className={`rounded-2xl border border-dashed p-10 text-center ${theme === "dark" ? "border-slate-700 text-slate-400" : "border-slate-300 text-slate-500"}`}>
            <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10 text-xl text-blue-400">◇</span>
            <p className="font-semibold">Your package catalog is empty</p>
            <p className="mt-1 text-sm">Create a package above to make a plan available to customers.</p>
          </div>
        )}
      </motion.section>
      </motion.div>
      )}

      {activeSection === "tickets" && (
      <motion.div
        key="tickets"
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -24 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
      <motion.section className={sectionClass} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
        <div className={`mb-6 overflow-hidden rounded-2xl border p-6 sm:p-8 ${
          theme === "dark" ? "border-blue-400/20 bg-gradient-to-br from-blue-950/70 via-slate-900 to-slate-900" : "border-blue-100 bg-gradient-to-br from-blue-50 to-white"
        }`}>
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-400/20 bg-blue-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-300">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
                Access credentials
              </div>
              <h2 className={`text-2xl font-extrabold sm:text-3xl ${theme === "dark" ? "text-white" : "text-slate-900"}`}>Ticket inventory</h2>
              <p className={`mt-2 max-w-2xl text-sm leading-relaxed ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                Add Wi-Fi credentials one at a time or import multiple tickets from a CSV file.
              </p>
            </div>
            <div className="flex gap-2">
              <div className={`rounded-xl border px-4 py-3 ${theme === "dark" ? "border-white/10 bg-white/[0.04]" : "border-blue-100 bg-white/80"}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Available</p>
                <p className="mt-1 text-xl font-extrabold text-emerald-500">{availableCredentials}</p>
              </div>
              <div className={`rounded-xl border px-4 py-3 ${theme === "dark" ? "border-white/10 bg-white/[0.04]" : "border-blue-100 bg-white/80"}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Assigned</p>
                <p className="mt-1 text-xl font-extrabold text-amber-500">{usedCredentials}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 grid items-start gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)]">
          <div className={`rounded-2xl border p-5 sm:p-6 ${
            theme === "dark" ? "border-slate-700/80 bg-slate-900/80" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"
          }`}>
            <div className="mb-5 flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-500">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </span>
              <div>
                <h3 className="font-bold">Add a ticket</h3>
                <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>Enter the login credentials and select the matching package.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label>
                <span className={`mb-1.5 block text-[10px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Username</span>
                <input
                  aria-label="Ticket username"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={`w-full rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-600 focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20"}`}
                />
              </label>
              <label>
                <span className={`mb-1.5 block text-[10px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Password</span>
                <input
                  aria-label="Ticket password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-600 focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20"}`}
                />
              </label>
              <label>
                <span className={`mb-1.5 block text-[10px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Package</span>
                <select
                  value={packageId}
                  onChange={(e) => setPackageId(e.target.value)}
                  className={`w-full rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"}`}
                >
                  {activePackages.map((p) => (
                    <option key={p.id} value={p.id} className="text-black">{p.name}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className={`mb-1.5 block text-[10px] font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Description <span className="normal-case font-normal text-slate-500">optional</span></span>
                <input
                  aria-label="Ticket description"
                  placeholder="Add a note"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={`w-full rounded-xl border px-3.5 py-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-600 focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20"}`}
                />
              </label>
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setUsername("");
                  setPassword("");
                  setDescription("");
                }}
                className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${theme === "dark" ? "border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"}`}
              >
                Clear form
              </button>
              <button
                type="button"
                onClick={addCredential}
                disabled={processing}
                className="rounded-xl bg-blue-700 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-900/20 transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {processing ? "Adding ticket…" : "Add ticket"}
              </button>
            </div>
          </div>

          <div className={`rounded-2xl border p-5 sm:p-6 ${
            theme === "dark" ? "border-slate-700/80 bg-slate-900/80" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"
          }`}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold">Bulk import</h3>
                <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>Upload ticket details from a CSV file.</p>
              </div>
              <span className="rounded-lg bg-emerald-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600">CSV</span>
            </div>
            <button
              type="button"
              onClick={downloadCsvTemplate}
              className={`mb-4 w-full rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${theme === "dark" ? "border-blue-500/20 bg-blue-500/10 text-blue-300 hover:bg-blue-500/15" : "border-blue-200 bg-blue-50 text-blue-800 hover:border-blue-300 hover:bg-blue-100"}`}
            >
              Download CSV template
            </button>
            <label className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-4 py-6 text-center transition ${
              theme === "dark" ? "border-slate-700 bg-slate-950/40 hover:border-blue-500/50 hover:bg-blue-500/5" : "border-slate-300 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/70"
            }`}>
              <svg viewBox="0 0 24 24" className="mb-2 h-7 w-7 text-blue-500" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M12 16V4m0 0L7 9m5-5 5 5M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
              </svg>
              <span className="text-sm font-semibold">Choose a CSV file</span>
              <span className="mt-1 text-[11px] text-slate-500">username, password, packageId, description</span>
              <input type="file" accept=".csv,text/csv" onChange={handleCSVUpload} className="sr-only" />
            </label>
            {csvErrors.length > 0 && (
              <div className="mt-3 rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-xs text-rose-500">
                <strong>CSV problems:</strong>
                <ul className="mt-1 list-disc pl-5">
                  {csvErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {/* Credentials Table with Filters & Pagination */}
      <motion.section className={sectionClass} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
            <h2 className={`text-xl font-bold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>Ticket directory</h2>
            <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>
              {filteredCredentials.length} matching {filteredCredentials.length === 1 ? "ticket" : "tickets"}
            </p>
            </div>
            <button
              type="button"
              onClick={deleteInactivePackageCredentials}
              disabled={!packagesLoaded || loadingCreds || processing || inactivePackageCredentials.length === 0}
              className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
                theme === "dark"
                  ? "border-rose-500/20 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
                  : "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100"
              }`}
            >
              {processing ? "Cleaning up…" : `Delete inactive tickets (${inactivePackageCredentials.length})`}
            </button>
          </div>

          <div className="grid gap-2 sm:grid-cols-3">
            <input
              aria-label="Search ticket username"
              placeholder="Search username"
              value={credSearch}
              onChange={(e) => {
                setCredSearch(e.target.value);
                setCredCurrentPage(1);
              }}
              className={`min-w-0 rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-2 sm:w-44 ${
                theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20"
              }`}
            />

            <select
              value={credPackageFilter}
              onChange={(e) => {
                setCredPackageFilter(e.target.value);
                setCredCurrentPage(1);
              }}
              aria-label="Filter tickets by package"
              className={`min-w-0 rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-2 ${
                theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"
              }`}
            >
              <option value="">All active packages</option>
              {activePackages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <select
              value={credUsedFilter}
              onChange={(e) => {
                setCredUsedFilter(e.target.value);
                setCredCurrentPage(1);
              }}
              aria-label="Filter tickets by usage status"
              className={`min-w-0 rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-2 ${
                theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"
              }`}
            >
              <option value="all">All</option>
              <option value="unused">Unused</option>
              <option value="used">Used</option>
            </select>
          </div>
        </div>

        {loadingCreds ? (
          <div className="flex items-center justify-center gap-3 py-16 text-sm text-slate-400">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
            Loading tickets…
          </div>
        ) : (
          <>
            <div className={`overflow-x-auto rounded-xl border ${theme === "dark" ? "border-slate-700/80" : "border-slate-200"}`}>
              <table className={tableClass}>
                <thead className={theme === "dark" ? "border-b border-slate-700 bg-slate-950/70 text-slate-400" : "border-b border-slate-200 bg-slate-50 text-slate-500"}>
                  <tr className="text-left text-[10px] uppercase tracking-wider">
                    <th className="px-4 py-3">Username</th>
                    <th className="px-4 py-3">Password</th>
                    <th className="px-4 py-3">Package</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Assigned to</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {credCurrentSlice.map((c) => (
                    <motion.tr
                      key={c.id}
                      className={theme === "dark" ? "border-b border-slate-700/70 transition-colors hover:bg-white/[0.025]" : "border-b border-slate-200 transition-colors hover:bg-slate-50"}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold">{c.username}</td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">{c.password}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs">{c.packageId}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${c.used ? "bg-amber-500/10 text-amber-600" : "bg-emerald-500/10 text-emerald-600"}`}>
                          {c.used ? "Assigned" : "Available"}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{c.assignedTo || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                        <button onClick={() => resetCredential(c.id)} className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition ${theme === "dark" ? "border-amber-500/20 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"}`}>
                          Reset
                        </button>
                        <button onClick={() => deleteCredential(c.id)} className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition ${theme === "dark" ? "border-rose-500/20 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20" : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"}`}>
                          Delete
                        </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                  {credCurrentSlice.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-12 text-center text-sm text-slate-500">
                        No tickets match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Credentials Pagination */}
            {credTotalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-4">
                <button onClick={() => setCredCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={credCurrentPage === 1} className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${theme === "dark" ? "border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"}`}>
                  Prev
                </button>

                <span className={theme === "dark" ? "text-slate-300" : "text-gray-700"}>
                  Page {credCurrentPage} of {credTotalPages}
                </span>

                <button onClick={() => setCredCurrentPage((prev) => Math.min(prev + 1, credTotalPages))} disabled={credCurrentPage === credTotalPages} className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${theme === "dark" ? "border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"}`}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </motion.section>
      </motion.div>
      )}

      {activeSection === "payments" && (
      <motion.div
        key="payments"
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -24 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
      <motion.section className={sectionClass} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
          <h2 className={`text-2xl font-semibold ${theme === "dark" ? "text-cyan-300" : "text-blue-800"}`}>
              Payments & ticket delivery
          </h2>
            <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-600"}`}>
              Find customer payments, check pending orders, and resend issued tickets by SMS.
            </p>
          </div>
          <button onClick={deleteAllTransactions} className="bg-rose-700 px-4 py-2 rounded-full text-sm font-semibold text-white hover:bg-rose-600 transition-all">
            Delete All
          </button>
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-3">
          <input
            value={paymentSearch}
            onChange={(event) => {
              setPaymentSearch(event.target.value);
              setTransCurrentPage(1);
            }}
            placeholder="Search name, phone, reference, ticket…"
            className={`min-w-[240px] flex-1 rounded-lg border p-2 outline-none ${
              theme === "dark" ? "border-slate-700 bg-gray-900/60 text-white" : "border-gray-300 bg-white text-black"
            }`}
          />
          <select
            value={transPackageFilter}
            onChange={(e) => {
              setTransPackageFilter(e.target.value);
              setTransCurrentPage(1);
            }}
            className={`p-2 rounded-lg outline-none ${
              theme === "dark" ? "bg-gray-900/60 border border-slate-700 text-white" : "bg-white border border-gray-400 text-black"
            }`}
          >
            <option value="">All packages</option>
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className={`mb-6 overflow-hidden rounded-xl border ${theme === "dark" ? "border-slate-700" : "border-gray-200"}`}>
          <div className={`border-b px-4 py-3 font-semibold ${theme === "dark" ? "border-slate-700 bg-slate-900/50" : "border-gray-200 bg-gray-50"}`}>
            Pending customer orders
          </div>
          {ordersLoading ? (
            <p className="p-4 text-sm text-slate-400">Loading orders…</p>
          ) : filteredOrders.filter((order) => order.status !== "fulfilled").length === 0 ? (
            <p className="p-4 text-sm text-slate-400">No pending orders match your search.</p>
          ) : (
            <div className="divide-y divide-slate-700/50">
              {filteredOrders
                .filter((order) => order.status !== "fulfilled")
                .map((order) => (
                  <div key={order.id} className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <div className="font-semibold">{order.name || "Customer"} · {order.phone}</div>
                      <div className="text-sm text-slate-400">
                        {order.packageName || order.packageId} · GHS {Number(order.amount || 0).toFixed(2)} · {formatTimestamp(order.createdAt)}
                      </div>
                      {order.paystackReference && (
                        <div className="mt-1 text-xs text-emerald-400">Paystack ref: {order.paystackReference}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        aria-label={`Payment date for ${order.name || order.phone}`}
                        type="date"
                        value={orderPaymentDates[order.id] || ""}
                        onChange={(event) => setOrderPaymentDates((current) => ({ ...current, [order.id]: event.target.value }))}
                        className={`rounded-lg border px-2 py-1.5 text-sm ${
                          theme === "dark" ? "border-slate-700 bg-slate-950 text-white" : "border-gray-300 bg-white text-slate-900"
                        }`}
                      />
                      <button
                        onClick={() => checkOrderPayment(order)}
                        disabled={checkingOrderId === order.id}
                        className="rounded-lg bg-cyan-700 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-600 disabled:opacity-50"
                      >
                        {checkingOrderId === order.id ? "Checking…" : order.paymentStatus === "verified" ? "Recheck payment" : "Check payment"}
                      </button>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                        order.paymentStatus === "verified" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"
                      }`}>
                        {order.paymentStatus === "verified" ? "Paid" : "Awaiting payment"}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteOrder(order)}
                        disabled={deletingOrderId === order.id}
                        className="rounded-lg bg-rose-700 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {deletingOrderId === order.id ? "Deleting…" : "Delete order"}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {loadingTrans ? (
          <p className={theme === "dark" ? "text-slate-400" : "text-gray-700"}>Loading transactions...</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className={tableClass}>
                <thead className={theme === "dark" ? "border-b border-slate-600 text-slate-300" : "border-b border-gray-400 text-gray-700"}>
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Phone</th>
                    <th className="px-3 py-2">Package</th>
                    <th className="px-3 py-2">Amount</th>
                    <th className="px-3 py-2">Ticket</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {transCurrentSlice.map((t) => (
                    <motion.tr
                      key={t.id}
                      className={theme === "dark" ? "border-b border-slate-700 hover:bg-slate-700/40" : "border-b border-gray-400 hover:bg-gray-200"}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <td className="px-3 py-2">{t.name}</td>
                      <td className="px-3 py-2">{t.phone}</td>
                      <td className="px-3 py-2">{t.packageId}</td>
                      <td className="px-3 py-2">GHS {t.amount}</td>
                      <td className="px-3 py-2">{t.credentials?.[0]?.username || t.username || "-"}</td>
                      <td className="px-3 py-2">{formatTimestamp(t.assignedAt || t.createdAt || t.timestamp || t.date)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => resendTicket(t)}
                            disabled={resendingReference === t.reference}
                            className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold hover:bg-emerald-500 disabled:opacity-50"
                          >
                            {resendingReference === t.reference ? "Sending…" : "Resend ticket"}
                          </button>
                          <button onClick={() => deleteTransaction(t.id)} className="bg-red-600 px-3 py-1 rounded hover:bg-red-500 text-xs font-semibold">
                            Delete
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                  {transCurrentSlice.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-3 py-8 text-center text-slate-400">
                        No payment records match this search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Transactions Pagination */}
            {transTotalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-4">
                <button onClick={() => setTransCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={transCurrentPage === 1} className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${theme === "dark" ? "border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"}`}>
                  Prev
                </button>

                <span className={theme === "dark" ? "text-slate-300" : "text-gray-700"}>
                  Page {transCurrentPage} of {transTotalPages}
                </span>

                <button onClick={() => setTransCurrentPage((prev) => Math.min(prev + 1, transTotalPages))} disabled={transCurrentPage === transTotalPages} className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${theme === "dark" ? "border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"}`}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </motion.section>
      </motion.div>
      )}
      </AnimatePresence>

      <div className="mt-6 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            const currentIndex = adminSections.findIndex((section) => section.id === activeSection);
            setActiveSection(adminSections[Math.max(currentIndex - 1, 0)].id);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          disabled={activeSection === adminSections[0].id}
          className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
            theme === "dark" ? "bg-slate-800 text-white hover:bg-slate-700" : "bg-white text-slate-800 hover:bg-gray-100"
          }`}
        >
          ← Previous
        </button>
        <span className={theme === "dark" ? "text-sm text-slate-400" : "text-sm text-slate-600"}>
          Page {adminSections.findIndex((section) => section.id === activeSection) + 1} of {adminSections.length}
        </span>
        <button
          type="button"
          onClick={() => {
            const currentIndex = adminSections.findIndex((section) => section.id === activeSection);
            setActiveSection(adminSections[Math.min(currentIndex + 1, adminSections.length - 1)].id);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          disabled={activeSection === adminSections[adminSections.length - 1].id}
          className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
            theme === "dark" ? "bg-cyan-700 text-white hover:bg-cyan-600" : "bg-blue-700 text-white hover:bg-blue-600"
          }`}
        >
          Next →
        </button>
      </div>
      </main>
    </div>
  );
}
